import { useSupportSession } from './SupportSessionContext';
import { navigateAdminSession } from './navigation';
import { useEffect, useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { ShieldCheck } from 'lucide-react';
import { adminWorkspaceApi, type AdminImpersonation } from './api';
import './AdminWorkspace.css';

export default function ImpersonationBanner() {
  const { isSignedIn, sessionId } = useAuth();
  const clerk = useClerk();
  const support = useSupportSession();
  const [current, setCurrent] = useState<AdminImpersonation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!current) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [current]);
  useEffect(() => {
    let active = true;
    setCurrent(null);
    setError('');
    if (isSignedIn && sessionId && clerk.session?.actor) {
      adminWorkspaceApi
        .currentImpersonation()
        .then((data) => {
          if (active && data.active) setCurrent(data.impersonation);
        })
        .catch(() => {
          // Keep actor sessions visible even if the attribution service is temporarily unavailable.
          if (active && clerk.session?.actor)
            setError('Support session details are unavailable. Exit to return to administration.');
        });
    }
    return () => {
      active = false;
    };
  }, [isSignedIn, sessionId, clerk]);
  const session = support?.current || current;
  const message = support?.error || error;
  if (!session && !message) return null;
  return (
    <aside className="admin-impersonation-banner" aria-label="Active support session">
      <ShieldCheck size={21} aria-hidden="true" />
      <div>
        <strong>{session ? `Viewing as ${session.target.name}` : 'Active support session'}</strong>
        <span>
          {session ? `${session.target.email} · Support by ${session.actor.name}` : message}
        </span>
        {session && (
          <small>
            {session.reason} ·{' '}
            {Date.parse(session.expiresAt) <= now
              ? 'Session expired — exit to return to administration'
              : `Ends ${new Date(session.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
          </small>
        )}
      </div>
      <button
        type="button"
        disabled={busy || support?.busy}
        onClick={async () => {
          if (support?.current?.mode === 'app') {
            await support.exit();
            return;
          }
          setBusy(true);
          try {
            const result = await adminWorkspaceApi.endImpersonation();
            if (result.resumeSessionId) {
              await clerk.setActive({ session: result.resumeSessionId });
              navigateAdminSession('/admin');
            } else navigateAdminSession(result.signInUrl || '/sign-in?redirect_url=%2Fadmin');
          } catch (err) {
            setError(
              err instanceof Error ? err.message : 'Unable to end the support session. Try again.'
            );
            setBusy(false);
          }
        }}
      >
        {busy || support?.busy ? 'Ending session…' : 'Exit support session'}
      </button>
      {session && message && <p role="alert">{message}</p>}
    </aside>
  );
}
